import { Eye, EyeOff, X } from 'lucide-react';
import { useCallback, useRef, useState, type ComponentPropsWithRef, type ReactNode } from 'react';

import { cx } from '@/shared/lib/cx';
import { useFieldContext } from '@/shared/ui/Field/FieldContext';

import styles from './Input.module.scss';

export type InputSize = 'sm' | 'md' | 'lg';

const SIZE: Record<InputSize, string> = {
  sm: styles['input--sm'] ?? '',
  md: styles['input--md'] ?? '',
  lg: styles['input--lg'] ?? '',
};

/**
 * Меняет значение поля так, как если бы его набрал человек.
 *
 * Простое присваивание `node.value` React не замечает: он подменяет сеттер
 * свойства, чтобы отслеживать изменения. Поэтому нужно вызвать исходный
 * сеттер и разослать событие — иначе кнопка очистки визуально сработает,
 * а форма продолжит считать, что в поле осталось старое значение.
 */
function setNativeValue(node: HTMLInputElement, value: string) {
  // Сеттер не сохраняем в переменную, а вызываем сразу через call: так он
  // не отвязывается от объекта и линтер не считает это ошибкой области this.
  Object.getOwnPropertyDescriptor(HTMLInputElement.prototype, 'value')?.set?.call(node, value);
  node.dispatchEvent(new Event('input', { bubbles: true }));
}

export interface InputProps extends Omit<ComponentPropsWithRef<'input'>, 'size' | 'prefix'> {
  inputSize?: InputSize;
  /** Неинтерактивная приставка внутри рамки: `https://`, иконка поиска. */
  prefix?: ReactNode;
  /** Неинтерактивный суффикс: единица измерения, доменная зона. */
  suffix?: ReactNode;
  /** Показывать кнопку очистки, когда в поле что-то есть. */
  clearable?: boolean;
  /** Счётчик символов. Осмыслен вместе с `maxLength`. */
  showCount?: boolean;
  /** Моноширинный шрифт — для ключей схем, слагов и идентификаторов. */
  mono?: boolean;
  /** Помечает поле ошибочным вне обёртки `Field`. */
  invalid?: boolean;
}

export function Input({
  inputSize = 'md',
  prefix,
  suffix,
  clearable = false,
  showCount = false,
  mono = false,
  invalid,
  className,
  type = 'text',
  id,
  disabled,
  required,
  maxLength,
  value,
  defaultValue,
  onChange,
  ref,
  'aria-describedby': ariaDescribedBy,
  ...rest
}: InputProps) {
  const field = useFieldContext();
  const innerRef = useRef<HTMLInputElement | null>(null);
  const [revealed, setRevealed] = useState(false);
  const [length, setLength] = useState(() => String(value ?? defaultValue ?? '').length);

  const setRefs = useCallback(
    (node: HTMLInputElement | null) => {
      innerRef.current = node;

      if (typeof ref === 'function') {
        ref(node);
      } else if (ref) {
        ref.current = node;
      }
    },
    [ref],
  );

  const isInvalid = invalid ?? field?.invalid ?? false;
  const isDisabled = disabled ?? field?.disabled ?? false;
  const isPassword = type === 'password';
  const currentLength = value !== undefined ? String(value).length : length;
  const hasValue = currentLength > 0;

  const handleChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setLength(event.target.value.length);
    onChange?.(event);
  };

  const handleClear = () => {
    const node = innerRef.current;

    if (!node) {
      return;
    }

    setNativeValue(node, '');
    setLength(0);
    // Возвращаем фокус в поле: после очистки человек почти всегда набирает
    // заново, и заставлять его снова целиться в поле незачем.
    node.focus();
  };

  const showClear = clearable && hasValue && !isDisabled;
  const showCounter = showCount && maxLength !== undefined;

  return (
    <div
      className={cx(
        styles['input'],
        SIZE[inputSize],
        isInvalid && styles['input--invalid'],
        isDisabled && styles['input--disabled'],
        className,
      )}
    >
      {prefix !== undefined && (
        <span className={styles['input__affix']} aria-hidden="true">
          {prefix}
        </span>
      )}

      <input
        {...rest}
        ref={setRefs}
        id={id ?? field?.controlId}
        type={isPassword && revealed ? 'text' : type}
        className={cx(styles['input__control'], mono && styles['input__control--mono'])}
        disabled={isDisabled}
        required={required ?? field?.required}
        maxLength={maxLength}
        value={value}
        defaultValue={defaultValue}
        onChange={handleChange}
        aria-invalid={isInvalid || undefined}
        aria-describedby={ariaDescribedBy ?? field?.describedBy}
      />

      {showCounter && (
        <span
          className={cx(
            styles['input__counter'],
            currentLength >= maxLength && styles['input__counter--full'],
          )}
        >
          {currentLength}/{maxLength}
        </span>
      )}

      {showClear && (
        <button
          type="button"
          className={styles['input__action']}
          onClick={handleClear}
          aria-label="Очистить поле"
          tabIndex={-1}
        >
          <X size={15} />
        </button>
      )}

      {isPassword && (
        <button
          type="button"
          className={styles['input__action']}
          onClick={() => {
            setRevealed((v) => !v);
          }}
          aria-label={revealed ? 'Скрыть пароль' : 'Показать пароль'}
          aria-pressed={revealed}
          disabled={isDisabled}
        >
          {revealed ? <EyeOff size={15} /> : <Eye size={15} />}
        </button>
      )}

      {suffix !== undefined && (
        <span className={styles['input__affix']} aria-hidden="true">
          {suffix}
        </span>
      )}
    </div>
  );
}
